// frontend/src/pages/admin/ProspectCspe.jsx
import React, { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { FiSearch, FiRefreshCw, FiUserPlus, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useProspectCspe } from '../../hooks/useProspectCspe';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    // Format compact dd/mm/yy pour économiser l'espace horizontal.
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return iso; }
};

export default function ProspectCspe() {
  const { session } = useOutletContext();
  const navigate = useNavigate();
  const {
    data, loading, error, refetch,
    page, setPage, pageSize, totalCount, totalPages,
  } = useProspectCspe(session, 50);

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Filtre + tri appliqués UNIQUEMENT sur la page courante (côté serveur pour
  // pagination sur 10k prospects). Le search est local à la page — pour un
  // vrai search cross-pages, il faudrait le déporter côté API (chantier futur).
  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = data;
    if (q) {
      rows = data.filter(r =>
        (r.company_name || '').toLowerCase().includes(q) ||
        (r.siren || '').toLowerCase().includes(q) ||
        (r.siret || '').toLowerCase().includes(q) ||
        (r.naf_code || '').toLowerCase().includes(q) ||
        (r.owner_name || '').toLowerCase().includes(q) ||
        (r.contact?.email || '').toLowerCase().includes(q) ||
        (r.contact?.nom || '').toLowerCase().includes(q)
      );
    }
    return [...rows].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === 'created_at') { va = va || ''; vb = vb || ''; }
      else { va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, search, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };
  const sortArrow = (col) => sortBy === col ? (sortDir === 'asc' ? '▲' : '▼') : '';

  const openAccount = (row) => {
    // Ligne cliquable : navigate vers ClientDetailPage (opportunity_id de préférence,
    // sinon fallback via account_id pour ne pas bloquer l'accès).
    if (row.opportunity_id) {
      navigate(`/dashboard/admin/client/${row.opportunity_id}`);
    } else {
      // Pas d'opportunité : passer l'account_id, ClientDetailPage sait résoudre.
      navigate(`/dashboard/admin/client/${row.id}?byAccount=1`);
    }
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50 p-3">
      <div className="max-w-7xl mx-auto space-y-3">
        {/* Header */}
        <div className="bg-white rounded shadow border border-gray-200 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiUserPlus className="h-4 w-4 text-emerald-600" />
            <div>
              <div className="text-sm font-bold text-gray-900">Prospects CSPE</div>
              <div className="text-[10px] text-gray-500">
                Comptes éligibles à la CSPE sans contrat CSPE signé
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] text-gray-500">
              <span className="font-bold text-emerald-700">{totalCount}</span> prospect{totalCount > 1 ? 's' : ''}
            </div>
            <button
              onClick={refetch}
              disabled={loading}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Rafraîchir"
            >
              <FiRefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded shadow border border-gray-200 px-3 py-2">
          <div className="relative">
            <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom société, SIREN, SIRET, NAF, email contact… (dans la page courante)"
              aria-label="Rechercher un prospect CSPE"
              className="w-full pl-7 pr-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
          {error && (
            <div className="px-3 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700">
              Erreur : {error}
            </div>
          )}
          {loading && !data.length ? (
            <div className="px-3 py-8 text-center text-xs text-gray-500">Chargement…</div>
          ) : displayed.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-gray-500">
              {data.length === 0
                ? 'Aucun prospect CSPE (tous les comptes éligibles ont déjà signé, ou aucun compte éligible).'
                : 'Aucun résultat pour cette recherche (dans la page courante).'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      onClick={() => toggleSort('company_name')}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggleSort('company_name'))}
                      tabIndex={0} role="button"
                      aria-sort={sortBy === 'company_name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      Société {sortArrow('company_name')}
                    </th>
                    <th
                      onClick={() => toggleSort('siren')}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggleSort('siren'))}
                      tabIndex={0} role="button"
                      aria-sort={sortBy === 'siren' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      SIREN {sortArrow('siren')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">NAF</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Téléphone</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Propriétaire</th>
                    <th
                      onClick={() => toggleSort('created_at')}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggleSort('created_at'))}
                      tabIndex={0} role="button"
                      aria-sort={sortBy === 'created_at' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      Créé le {sortArrow('created_at')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayed.map((row) => {
                    const tel = row.contact?.telephone_portable || row.contact?.telephone_fixe || null;
                    return (
                      <tr
                        key={row.id}
                        onClick={() => openAccount(row)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), openAccount(row))}
                        tabIndex={0}
                        role="button"
                        aria-label={`Voir le détail du compte ${row.company_name}`}
                        className="hover:bg-emerald-50 cursor-pointer focus:outline-none focus:bg-emerald-50 transition-colors"
                        title="Cliquer pour voir le détail du compte"
                      >
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <div className="text-[11px] font-semibold text-gray-900">{row.company_name || '—'}</div>
                          {row.siret && <div className="text-[9px] text-gray-400 font-mono">{row.siret}</div>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[11px] text-gray-700 font-mono">{row.siren || '—'}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[11px] text-gray-700 font-mono">{row.naf_code || '—'}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[11px] text-gray-800">
                          {row.contact
                            ? `${row.contact.prenom || ''} ${row.contact.nom || ''}`.trim() || '—'
                            : <span className="text-gray-400 italic">—</span>}
                          {row.contact?.fonction && <div className="text-[9px] text-gray-400">{row.contact.fonction}</div>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[10px]">
                          {row.contact?.email ? (
                            <a
                              href={`mailto:${row.contact.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-emerald-700 hover:underline"
                            >
                              {row.contact.email}
                            </a>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[10px]">
                          {tel ? (
                            <a
                              href={`tel:${tel}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-gray-700 hover:underline"
                            >
                              {tel}
                            </a>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[10px] text-gray-700">
                          {row.owner_name || <span className="text-gray-400 italic">—</span>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[10px] text-gray-500">{fmtDate(row.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200 text-[11px]">
              <div className="text-gray-600">
                {rangeStart}–{rangeEnd} sur <span className="font-semibold">{totalCount}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1 || loading}
                  className="px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Première page"
                >«</button>
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1 || loading}
                  className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft className="h-3 w-3" /> Précédent
                </button>
                <span className="px-2 text-gray-700">
                  Page <span className="font-semibold">{page}</span> / {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages || loading}
                  className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant <FiChevronRight className="h-3 w-3" />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages || loading}
                  className="px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Dernière page"
                >»</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
